import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { AuthProvider } from './auth';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/toast';
import { installGlobalErrorHandlers } from './diagnostics';
import { I18nProvider, detectInitialLocale, loadCatalog } from './i18n';
import { ThemeProvider } from './theme';
import './index.css';

// Capture des erreurs globales (exceptions + rejets non gérés) AVANT tout rendu :
// la cause d'un crash précoce est ainsi présente dans l'anneau de diagnostic.
installGlobalErrorHandlers();

const root = document.getElementById('root');
if (!root) throw new Error('Élément #root introuvable');

/**
 * Langue résolue AVANT le premier rendu (localStorage → système → `en`), exactement comme le
 * script anti-flash d'`index.html` — qui a d'ailleurs déjà lancé le TÉLÉCHARGEMENT du chunk
 * correspondant via un `modulepreload` (cf. `vite-plugin-locale-preload.ts`). Le catalogue
 * est donc, en pratique, déjà dans le cache HTTP quand on le demande ici : l'attente
 * ci-dessous ne coûte pas d'aller-retour réseau supplémentaire.
 */
const locale = detectInitialLocale();

/**
 * On rend APRÈS résolution du catalogue : `t()` est alors synchrone dès le premier rendu, et
 * aucun texte non traduit n'apparaît à l'écran. En cas d'échec de chargement on rend quand
 * même — une app en clés brutes reste utilisable, une page blanche non.
 */
void loadCatalog(locale)
  .catch(() => undefined)
  .then(() => {
    mount(root);
  });

function mount(container: HTMLElement): void {
  createRoot(container).render(
    <StrictMode>
      <ThemeProvider>
        {/* La langue est passée EXPLICITEMENT : celle qu'on vient de charger, jamais une
            seconde détection qui pourrait diverger de la première. */}
        <I18nProvider initialLocale={locale}>
          <ToastProvider>
            <ErrorBoundary>
              <BrowserRouter>
                <AuthProvider>
                  <App />
                </AuthProvider>
              </BrowserRouter>
            </ErrorBoundary>
          </ToastProvider>
        </I18nProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}
